import { useCallback, useState } from "react";
import {
  generateAndDownloadExcel,
  IExcelDownloadDefinitionWithFormat,
} from "@features/assets-feature/helpers/excelGenerationHelpers";

interface UseGridExcelExportProps<TRow extends object> {
  rows: TRow[];
  definitions: IExcelDownloadDefinitionWithFormat[];
  filename: string | (() => string);
  mapRow?: (row: TRow) => Record<string, unknown>;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  onEmpty?: () => void;
}

interface UseGridExcelExportReturn {
  isExporting: boolean;
  handleExport: () => Promise<void>;
}

function useGridExcelExport<TRow extends object>({
  rows,
  definitions,
  filename,
  mapRow,
  onSuccess,
  onError,
  onEmpty,
}: UseGridExcelExportProps<TRow>): UseGridExcelExportReturn {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (rows.length === 0) {
      onEmpty?.();
      return;
    }
    setIsExporting(true);
    try {
      const exportRows = rows.map((row) => (mapRow ? mapRow(row) : { ...row }));
      const resolvedFilename =
        typeof filename === "function" ? filename() : filename;
      await generateAndDownloadExcel(exportRows, definitions, resolvedFilename);
      onSuccess?.();
    } catch (error) {
      onError?.(error);
    } finally {
      setIsExporting(false);
    }
  }, [rows, definitions, filename, mapRow, onSuccess, onError, onEmpty]);

  return { isExporting, handleExport };
}

export default useGridExcelExport;